import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useMemo, useState } from 'react'
import {
  SpectrumBankAccountOperation,
  SpectrumBankAccountStatus,
  SpectrumIdentityTypeEnum,
} from '../../../../common/enums/spectrumBankAccountEnum'
import {
  ISpectrumBankAccount,
  ISpectrumBankAccountApproveRejectRequest,
  ISpectrumBankAccountGlobalResult,
} from '../../../../common/interfaces'
import { SpectrumBankAccountService } from '../../../../services'

export interface ISpectrumBankAccountFilters {
  tipoIdentidad: SpectrumIdentityTypeEnum | ''
  numIdentidad: string
  estado: SpectrumBankAccountStatus | ''
}

export const initialSpectrumBankAccountFilters: ISpectrumBankAccountFilters = {
  tipoIdentidad: '',
  numIdentidad: '',
  estado: '',
}

export interface ISpectrumBankAccountSelection {
  codCliente: string
  tipoIdentidad: SpectrumIdentityTypeEnum | ''
  numIdentidad: string
  numSecBancario: number
  estado: SpectrumBankAccountStatus | ''
}

const SPECTRUM_BANK_ACCOUNTS_QUERY_KEY = 'spectrum-bank-accounts'

export const useSpectrumBankAccounts = (
  filters: ISpectrumBankAccountFilters,
  page: number,
  limit: number
) => {
  const queryClient = useQueryClient()
  const service = SpectrumBankAccountService()
  const [selected, setSelected] = useState<ISpectrumBankAccountSelection | null>(null)

  const isByCustomer = Boolean(filters.tipoIdentidad && filters.numIdentidad)

  const query = useQuery<
    {
      data: (ISpectrumBankAccount | ISpectrumBankAccountGlobalResult)[]
      total: number
      limit: number
      page: number
    },
    AxiosError
  >({
    queryKey: [SPECTRUM_BANK_ACCOUNTS_QUERY_KEY, JSON.stringify(filters), page, limit],
    queryFn: () =>
      isByCustomer
        ? service.searchByCustomer({
            tipoIdentidad: filters.tipoIdentidad as SpectrumIdentityTypeEnum,
            numIdentidad: filters.numIdentidad,
            numeroPagina: page,
            registrosPorPagina: limit,
            estado: filters.estado || undefined,
          })
        : service.search({
            numeroPagina: page,
            registrosPorPagina: limit,
            estado: filters.estado || undefined,
          }),
    keepPreviousData: true,
  })

  const isSameRow = (a: ISpectrumBankAccountSelection, b: ISpectrumBankAccountSelection) =>
    a.codCliente === b.codCliente && a.numSecBancario === b.numSecBancario

  const toggleSelected = (row: ISpectrumBankAccountSelection) => {
    setSelected((prev) => (prev && isSameRow(prev, row) ? null : row))
  }

  const clearSelected = () => setSelected(null)

  const approveRejectMutation = useMutation({
    mutationFn: (req: ISpectrumBankAccountApproveRejectRequest) => service.approveReject(req),
  })

  const approveRejectSelected = async (operation: SpectrumBankAccountOperation) => {
    if (!selected) return null

    const req: ISpectrumBankAccountApproveRejectRequest = {
      tipoIdentidad: selected.tipoIdentidad as SpectrumIdentityTypeEnum,
      numIdentidad: selected.numIdentidad,
      numSecBancario: selected.numSecBancario,
      tipoOperacion: operation,
    }

    const outcome = await approveRejectMutation
      .mutateAsync(req)
      .then((res) => ({
        numSecBancario: res.numSecBancario,
        numIdentidad: res.numIdentidad,
        ok: res.success,
        message: res.message,
      }))
      .catch((error) => {
        const axiosError = error as AxiosError<{ message?: string }>
        return {
          numSecBancario: selected.numSecBancario,
          numIdentidad: selected.numIdentidad,
          ok: false as const,
          message: axiosError.response?.data?.message || 'Error al procesar la cuenta.',
        }
      })

    await queryClient.invalidateQueries({ queryKey: [SPECTRUM_BANK_ACCOUNTS_QUERY_KEY] })
    clearSelected()

    return outcome
  }

  const canOperate = useMemo(
    () => selected !== null && selected.estado === SpectrumBankAccountStatus.PENDING,
    [selected]
  )

  return {
    query,
    isByCustomer,
    selected,
    toggleSelected,
    clearSelected,
    approveRejectSelected,
    isBulkActionLoading: approveRejectMutation.isLoading,
    canOperate,
  }
}
